import { createStyles, makeStyles } from "@material-ui/core";
import {
    FilterIcon,
    HelpIcon,
    MarketResearchConfigurationMobile,
    MarketResearchTool,
    MarketResearchType,
    MobilePage,
    MobilePageActions,
    MobilePageActionsProps,
    MobilePageSection,
    RelodeTheme,
    ShareAltIcon,
    Text,
} from "@relode/core";
import classnames from "classnames";
import * as React from "react";

import { useClientMarketResearchContext } from "./_infra/clientMarketResearchProvider";
import { ClientMarketResearchProps } from "./clientMarketResearchProps";
import { MarketResearchHelpSection } from "../../shared/components";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            configureHelperContainer: {
                alignItems: "center",
                backgroundColor: theme.palette.grey[100],
                borderRadius: 6,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                marginBottom: theme.spacing(2),
                minHeight: 230,
                width: "100%",
            },
            configureHelperText: {
                color: theme.palette.grey[700],
            },
            criteria: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 0,
                marginRight: theme.spacing(4),
                width: 250,
            },
            marketResearchContainer: {
                padding: "16px",
            },
            results: {
                flexGrow: 1,
                padding: 20,
            },
            root: {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                padding: theme.spacing(0, 2),
            },
            sortAndFilterContainer: {
                display: "flex",
                marginRight: theme.spacing(2),
                position: "absolute",
                right: 0,
            },
            sortIcon: {
                paddingTop: "2px",
            },
            sortLink: {
                marginRight: theme.spacing(3),
            },
        }),
    { name: "ClientMarketResearchMobile" }
);

export const ClientMarketResearchMobile = (
    props: ClientMarketResearchProps
) => {
    const classes = useStyles(props);

    const [configurationDialogOpen, setConfigurationDialogOpen] =
        React.useState(false);
    const {
        onHelp,
        onShare,
        onSearch,
        onJobTitleChange,
        onLocationChange,
        searchCriteria: searchCriteriaProp,
        recentSearches = [],
    } = props;
    const [state] = useClientMarketResearchContext();

    const {
        searchCriteria,
        searchResults,
        searchResults: {
            summary: { isBusy: summaryIsBusy, data: summaryData },
        },
        searchMode,
    } = state;

    // Disable search button if these haven't changed
    const searchDisabled =
        (searchCriteriaProp?.jobName === searchCriteria?.jobName &&
            searchCriteriaProp?.locationId === searchCriteria?.locationId) ||
        !searchCriteriaProp?.jobName?.trim() ||
        !searchCriteriaProp?.locationId;

    const renderActionBar = (): React.ReactElement<MobilePageActionsProps> => (
        <MobilePageActions title="market research">
            <div className={classes.sortAndFilterContainer}>
                {!!summaryData ? (
                    <>
                        <HelpIcon
                            onClick={onHelp}
                            className={classnames(
                                classes.sortIcon,
                                classes.sortLink
                            )}
                        />
                        <ShareAltIcon
                            onClick={onShare}
                            className={classnames(
                                classes.sortIcon,
                                classes.sortLink
                            )}
                        />
                    </>
                ) : null}
                <FilterIcon onClick={() => setConfigurationDialogOpen(true)} />
            </div>
        </MobilePageActions>
    );

    const renderMarketResearchResults = () => {
        //default when there's no config - coming into a fresh instance of marketResearch
        if (searchMode === "configure") {
            return (
                <div className={classes.configureHelperContainer}>
                    <Text className={classes.configureHelperText}>
                        use the filter icon above to get your market research
                    </Text>
                </div>
            );
        }

        //marketResearch upon successful configuration/normalization
        return (
            <MarketResearchTool
                marketResearchType={MarketResearchType.PrivateTool}
                state={searchResults}
            />
        );
    };

    return (
        <>
            <MobilePage
                showTabs={false}
                pageTitle="market research"
                busyType="bar"
                defaultSection="clientMarketResearchMobileContainer"
                isBusy={summaryIsBusy}
                actions={renderActionBar()}
            >
                <MobilePageSection id="clientMarketResearchMobileContainer">
                    <div className={classes.marketResearchContainer}>
                        {renderMarketResearchResults()}
                    </div>
                </MobilePageSection>
            </MobilePage>
            <MarketResearchConfigurationMobile
                isOpen={configurationDialogOpen}
                onClose={() => setConfigurationDialogOpen(false)}
                onJobTitleChange={onJobTitleChange}
                onLocationChange={onLocationChange}
                onSearch={onSearch}
                searchCriteria={searchCriteriaProp}
                searchDisabled={searchDisabled}
                recentSearches={recentSearches}
            />
        </>
    );
};
